import { toast } from '@britishcouncil/react-admin';
import { getErrorMessage } from 'utils';

type MutationFunction<TArgs, TResult> = (args: TArgs) => {
  unwrap(): Promise<TResult>;
};

type UseSafeMutationOptions<TResult> = {
  successToastMessage?: string;
  errorToastMessage?: string;
  onSuccess?: (result: TResult) => void | Promise<void>;
  onError?: (errors: unknown) => void;
};

export const useSafeMutation = <TArgs, TResult>(
  mutationFn: MutationFunction<TArgs, TResult>,
  options: UseSafeMutationOptions<TResult> = {}
) => {
  const { onSuccess, onError, successToastMessage } = options;

  const execute = async (args: TArgs) => {
    try {
      const result = await mutationFn(args).unwrap();
      await onSuccess?.(result);

      if (successToastMessage) {
        toast({
          type: 'success',
          title: 'Success',
          message: successToastMessage,
        });
      }
      return result;
    } catch (error) {
      // log error to sentry via console log integration
      console.error(error);
      const errorMsg = getErrorMessage(error);

      if (options.errorToastMessage || errorMsg) {
        toast({
          type: 'error',
          title: 'Error',
          message: options.errorToastMessage ?? errorMsg,
        });
      }
      onError?.(error);
    }
  };

  return execute;
};
